import { useEffect, useRef, useState } from 'react';
import { toTimecode } from '../../utils/general';
import useRemConvert from '../../hooks/useRemConvert';

interface TimelineBarProps {
	timelineScale: number;
	parentScrollElement: HTMLElement | null;
}

const TimelineBar: React.FC<TimelineBarProps> = ({
	timelineScale,
	parentScrollElement,
}: TimelineBarProps) => {
	const [scrollLeft, setScrollLeft] = useState(0);
	const { remToPx, pxToRem } = useRemConvert();

	useEffect(() => {
		if (parentScrollElement === null) return;

		function onScroll(event: Event) {
			if (parentScrollElement === null) return;
			setScrollLeft(parentScrollElement.scrollLeft);
		}

		parentScrollElement.addEventListener('scroll', onScroll);
		return () => parentScrollElement.removeEventListener('scroll', onScroll);
	}, [parentScrollElement]);

	const containerRef = useRef<HTMLDivElement>(null);

	// Draw an extra screen's worth of the timeline bar on either side
	// so it's less noticeable when scrolling fast
	const minPos = -(containerRef.current?.getBoundingClientRect().left || 0) - window.innerWidth;
	const maxPos = minPos + window.innerWidth * 2;

	const tickTimeDistances  = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 60, 120, 300, 600, 1200, 1800, 3600];
	const smallTicksPerTicks = [  5,   4,   5, 5, 4, 5,  5,  4,  6,  6,   6,   5,   5,    5,    6,    6];
	const minTickDistance = remToPx(5);
	const idealTickTimeDistance = minTickDistance / timelineScale;
	let tickTimeDistanceIdx = tickTimeDistances.findIndex((dist) => dist > idealTickTimeDistance);
	if (tickTimeDistanceIdx < 0) tickTimeDistanceIdx = tickTimeDistances.length - 1;

	const tickTimeDistance: number = tickTimeDistances[tickTimeDistanceIdx];
	const smallTicksPerTick: number = smallTicksPerTicks[tickTimeDistanceIdx];

	const startTickIdx = Math.max(0, Math.floor(minPos / timelineScale / tickTimeDistance));
	const endTickIdx = Math.ceil(maxPos / timelineScale / tickTimeDistance) + 1;

	let ticksJsx = [];
	for (let tickIdx = startTickIdx; tickIdx < endTickIdx; tickIdx++) {
		for (let smallTickIdx = 1; smallTickIdx < smallTicksPerTick; smallTickIdx++) {
			const time = (tickIdx + smallTickIdx / smallTicksPerTick) * tickTimeDistance;
			const pos = time * timelineScale;
			ticksJsx.push(
				<div
					key={tickIdx.toString() + smallTickIdx.toString()}
					className='timelineSmallTick'
					style={{ left: pos }}
				></div>
			);
		}

		const time = tickIdx * tickTimeDistance;
		const pos = time * timelineScale;
		ticksJsx.push(
			<div
				key={`${tickIdx}timelineTick`}
				className='timelineTick'
				style={{ left: pos }}
			></div>
		);
		ticksJsx.push(
			<div
				key={`${tickIdx}timelineTickTimecode`}
				className='timelineTickTimecode'
				style={{ left: pos }}
			>
				{toTimecode(time)}
			</div>
		);
	}

	return <div ref={containerRef}>{ticksJsx}</div>;
};

export default TimelineBar;
